import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { RiskBadge } from "@/components/RiskBadge";

interface Row {
  id: string; gig_id: string; gig_title: string; amount_cents: number;
  opener_role: string; opened_by: string; ai_risk: "low" | "medium" | "high" | null;
  status: string; created_at: string; poster_id: string; worker_id: string | null; reason: string;
}
interface FullDispute extends Row {
  ai_summary: string | null; ai_recommended: string | null;
  evidence_urls: string[]; admin_notes: string | null;
}

const AdminDisputes = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<FullDispute | null>(null);
  const [resolution, setResolution] = useState<"release" | "refund" | "split">("release");
  const [splitAmount, setSplitAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("admin_dispute_queue");
    if (error) toast.error(error.message);
    setRows((data as Row[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openRow = async (r: Row) => {
    const { data } = await supabase.from("gig_disputes").select("*").eq("id", r.id).maybeSingle();
    setActive({ ...r, ...(data as any) });
  };

  const runAi = async () => {
    if (!active) return;
    setAiBusy(true);
    const { error } = await supabase.functions.invoke("dispute-ai-review", { body: { dispute_id: active.id } });
    setAiBusy(false);
    if (error) return toast.error(error.message);
    toast.success("AI review complete");
    const { data } = await supabase.from("gig_disputes").select("*").eq("id", active.id).maybeSingle();
    setActive((a) => (a ? { ...a, ...(data as any) } : a));
    load();
  };

  const resolve = async () => {
    if (!active) return;
    const splitCents = resolution === "split" ? Math.round(parseFloat(splitAmount || "0") * 100) : null;
    setBusy(true);
    const { error } = await supabase.rpc("admin_resolve_dispute", {
      _dispute_id: active.id,
      _resolution: resolution,
      _worker_payout_cents: splitCents,
      _admin_notes: notes || null,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Dispute resolved");
    setActive(null); setNotes(""); setSplitAmount("");
    load();
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-destructive" /> Dispute queue
        </h1>
        <p className="text-sm text-muted-foreground">Review evidence, run AI review, and resolve.</p>
      </div>

      {loading && <Card className="p-4 text-sm text-muted-foreground">Loading…</Card>}
      {!loading && rows.length === 0 && <Card className="p-4 text-sm text-muted-foreground">No disputes — all calm.</Card>}

      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map((r) => (
          <Card key={r.id} className="p-3 space-y-2 cursor-pointer hover:bg-muted/30" onClick={() => openRow(r)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{r.gig_title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">"{r.reason}"</p>
              </div>
              <Badge variant={r.status === "open" ? "destructive" : "secondary"}>{r.status.replace(/_/g, " ")}</Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <RiskBadge risk={r.ai_risk} />
              <span className="text-muted-foreground">{formatMoney(r.amount_cents)} · opened by {r.opener_role} · {new Date(r.created_at).toLocaleString()}</span>
            </div>
          </Card>
        ))}
      </div>

      {active && (
        <Card className="p-4 space-y-3 border-primary/30">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-base">{active.gig_title}</h2>
            <Button variant="ghost" size="sm" onClick={() => setActive(null)}>Close</Button>
          </div>
          <p className="text-xs text-muted-foreground">Opened by {active.opener_role} · {formatMoney(active.amount_cents)}</p>
          <div className="text-sm bg-muted/40 rounded p-2"><span className="font-semibold">Reason:</span> {active.reason}</div>

          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider">AI review</p>
              <Button size="sm" variant="outline" onClick={runAi} disabled={aiBusy}>
                {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Sparkles className="h-3 w-3 mr-1" /> Run AI</>}
              </Button>
            </div>
            <div className="flex items-center gap-2"><RiskBadge risk={active.ai_risk} />{active.ai_recommended && <Badge variant="outline">recommends: {active.ai_recommended}</Badge>}</div>
            {active.ai_summary && <p className="text-xs">{active.ai_summary}</p>}
          </div>

          {active.status === "open" || active.status === "under_review" ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider">Resolution</p>
              <div className="flex gap-2 flex-wrap">
                {(["release", "refund", "split"] as const).map((r) => (
                  <Button key={r} size="sm" variant={resolution === r ? "default" : "outline"} onClick={() => setResolution(r)}>{r}</Button>
                ))}
              </div>
              {resolution === "split" && (
                <Input
                  inputMode="decimal" placeholder="Worker payout in $ (e.g. 25.00)"
                  value={splitAmount} onChange={(e) => setSplitAmount(e.target.value)}
                />
              )}
              <Textarea placeholder="Admin notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
              <Button onClick={resolve} disabled={busy} className="w-full">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Resolve as ${resolution}`}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Already resolved.</p>
          )}
        </Card>
      )}
    </div>
  );
};

export default AdminDisputes;
